/**
 * Client-side SEO head reset for SPA (non-Topic, non-Chapter) routes.
 *
 * When the user navigates within the SPA to a route like /browse, /community,
 * /ai, /profile, etc., `showView()` calls `resetSpaDocumentSeo(pathname)` to
 * replace the document head with the correct title, meta, canonical, and
 * JSON-LD for that route. Without this, the head would retain whatever the
 * last Topic/Chapter page injected (via applyCourseDocumentSeo).
 *
 * JSON-LD handling per strategy:
 *  - "website": remove all JSON-LD, rebuild WebSite + Organization
 *  - "webpage": remove all JSON-LD, rebuild WebPage
 *  - "none":    remove all JSON-LD, do not rebuild
 *  - "topics":  remove only #course-structured-data, preserve any
 *    SSR-injected ItemList JSON-LD (which has no id attribute)
 *
 * The config values come from the shared SPA_SEO_MAP so they are identical
 * to what the server injects on F5 refresh. The robots value is used as-is
 * from SPA_SEO_MAP; in test environment the server already overrides it to
 * noindex via x-robots-tag + the fail-closed head, and on F5 in test the
 * server strips and re-injects. For SPA navigation, the client uses the
 * SPA_SEO_MAP robots value directly - in production this is correct
 * (index,follow for public pages, noindex for private), and in test the
 * server-side x-robots-tag header already enforces noindex regardless of
 * what the meta tag says.
 */

import { SPA_SEO_MAP, type JsonLdStrategy } from '../../shared/seo/spaSeoConfig';
import type { BrowseSeoPageModel } from '../../shared/pages/pageModels';
import { upsertMeta, removeMeta, upsertLink, bumpNavigationGeneration } from './courseDocumentSeo';
import { readBrowseSeoPageModel } from './browseSeoPageModel';

const BRAND_NAME = 'DeepMox';
const OG_IMAGE_PATH = '/assets/brand/deepmox-og-1200x630-v2.png';

/**
 * Reset document head for a SPA route. Called by showView() after
 * pushState, but NOT for topic/article/idea-detail views (those have
 * their own dedicated SEO sync functions).
 */
export function resetSpaDocumentSeo(pathname: string): void {
	const first = pathname.replace(/^\/+/, '').split('/')[0];
	const config = SPA_SEO_MAP[first] ?? SPA_SEO_MAP[''];
	if (!config) return;

	// Invalidate any in-flight course/idea SEO sync so it won't overwrite us.
	bumpNavigationGeneration();

	const origin = window.location.origin;
	const browseSeoPage = first === 'browse' ? readBrowseSeoPageModel() : null;
	const browseDefaultEquivalent = first === 'browse' && isDefaultBrowseEquivalentUrl();
	const browseVariant = first === 'browse' && browseSeoPage === null && !browseDefaultEquivalent && window.location.search !== '';
	const canonical = `${origin}${browseSeoPage?.canonicalPath ?? (browseVariant ? browseVariantCanonicalPath() : pathname)}`;
	const robots = browseSeoPage?.robots ?? (browseVariant ? 'noindex,follow' : config.robots);
	const image = new URL(OG_IMAGE_PATH, origin).href;

	// Title
	document.title = config.title;

	// Basic meta
	upsertMeta(document, 'name', 'description', config.description);
	upsertLink(document, 'canonical', canonical);
	upsertMeta(document, 'name', 'robots', robots);

	// Open Graph
	upsertMeta(document, 'property', 'og:type', config.ogType);
	upsertMeta(document, 'property', 'og:site_name', BRAND_NAME);
	upsertMeta(document, 'property', 'og:title', config.title);
	upsertMeta(document, 'property', 'og:description', config.description);
	upsertMeta(document, 'property', 'og:url', canonical);
	upsertMeta(document, 'property', 'og:image', image);
	upsertMeta(document, 'property', 'og:image:width', '1200');
	upsertMeta(document, 'property', 'og:image:height', '630');
	upsertMeta(document, 'property', 'og:image:alt', config.title);
	upsertMeta(document, 'property', 'og:locale', 'en_US');

	// Twitter Card
	upsertMeta(document, 'name', 'twitter:card', 'summary_large_image');
	upsertMeta(document, 'name', 'twitter:title', config.title);
	upsertMeta(document, 'name', 'twitter:description', config.description);
	upsertMeta(document, 'name', 'twitter:image', image);
	upsertMeta(document, 'name', 'twitter:image:alt', config.title);

	// Remove article-specific meta that may linger from a Chapter page.
	for (const prop of ['article:published_time', 'article:modified_time', 'article:section', 'article:author']) {
		removeMeta(document, 'property', prop);
	}

	// JSON-LD handling per strategy
	applyJsonLdStrategy(config.jsonLd, origin, canonical, config.title, browseSeoPage, browseVariant);
}

/** The legacy explicit spelling of the canonical all/newest directory. */
function isDefaultBrowseEquivalentUrl(): boolean {
	const params = new URLSearchParams(window.location.search);
	return params.size === 2
		&& params.getAll('category').length === 1
		&& params.get('category') === 'all'
		&& params.getAll('sort').length === 1
		&& params.get('sort') === 'newest';
}

/** Keep ordinary Browse variants out of the index without discarding a clean pagination canonical. */
function browseVariantCanonicalPath(): string {
	const params = new URLSearchParams(window.location.search);
	const pages = params.getAll('page');
	if (pages.length !== 1 || !/^\d+$/u.test(pages[0])) return '/browse';
	const page = Number(pages[0]);
	return Number.isSafeInteger(page) && page >= 2 ? `/browse?page=${page}` : '/browse';
}

/**
 * Apply the JSON-LD strategy for the current SPA route.
 *
 * - "website" / "webpage" / "none": remove ALL JSON-LD scripts, then
 *   optionally rebuild. This is safe because these routes never have
 *   SSR-injected ItemList data to preserve.
 * - "topics": remove ONLY #course-structured-data (the
 *   element id used by applyCourseDocumentSeo). Any other JSON-LD
 *   (e.g. the SSR-injected ItemList from buildSpaSeoInjection) is
 *   preserved so Googlebot's Wave 2 rendering sees the same structured
 *   data as Wave 1.
 */
function applyJsonLdStrategy(
	strategy: JsonLdStrategy,
	origin: string,
	canonical: string,
	title: string,
	browseSeoPage: BrowseSeoPageModel | null,
	browseVariant: boolean,
): void {
	switch (strategy) {
		case 'website': {
			removeAllJsonLd();
			const script = document.createElement('script');
			script.type = 'application/ld+json';
			script.textContent = JSON.stringify([
				{
					'@context': 'https://schema.org',
					'@type': 'WebSite',
					name: BRAND_NAME,
					url: origin,
					description: `${BRAND_NAME} helps you learn any topic chapter by chapter, through curated topics or private topics built from your sources.`,
				},
				{
					'@context': 'https://schema.org',
					'@type': 'Organization',
					name: BRAND_NAME,
					url: origin,
				},
			]);
			document.head.appendChild(script);
			break;
		}
		case 'webpage': {
			removeAllJsonLd();
			const script = document.createElement('script');
			script.type = 'application/ld+json';
			script.textContent = JSON.stringify({
				'@context': 'https://schema.org',
				'@type': 'WebPage',
				name: title,
				url: canonical,
				publisher: { '@type': 'Organization', name: BRAND_NAME },
			});
			document.head.appendChild(script);
			break;
		}
		case 'none': {
			// Remove all JSON-LD. The SSR "none" strategy emits {} (empty
			// object) which is invalid noise; removing it is a positive
			// improvement for Googlebot Wave 2 rendering.
			removeAllJsonLd();
			break;
		}
		case 'topics': {
			if (browseSeoPage) {
				removeAllJsonLd();
				const script = document.createElement('script');
				script.id = 'browse-page-item-list';
				script.type = 'application/ld+json';
				script.textContent = JSON.stringify({
					'@context': 'https://schema.org',
					'@type': 'ItemList',
					name: 'Browse Topics',
					itemListElement: browseSeoPage.topics.map((topic, index) => ({
						'@type': 'ListItem',
						position: index + 1,
						name: topic.title,
						url: `${origin}/topics/${encodeURIComponent(topic.slug)}`,
					})),
				});
				document.head.appendChild(script);
				break;
			}
			if (browseVariant) {
				removeAllJsonLd();
				break;
			}
			// A fixed page generated this id on an earlier SPA visit. Bare Browse
			// may retain its SSR ItemList (which has no id), but must not retain
			// that old page slice.
			document.getElementById('browse-page-item-list')?.remove();
			// Only remove the course-structured-data script (injected by
			// applyCourseDocumentSeo when navigating from a Topic/Chapter
			// page). Preserve any other JSON-LD (e.g. SSR ItemList).
			document.getElementById('course-structured-data')?.remove();
			break;
		}
	}
}

function removeAllJsonLd(): void {
	document.querySelectorAll('script[type="application/ld+json"]').forEach((node) => node.remove());
}
